import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './components/Header/Header';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from './app/store';
import './App.less';
import { LoaderProvider } from './hooks/loadingContext';
import ActionError from './utils/ActionError';
import { Notify } from './services/notificationService';
import { Layout, Result, Button } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import IntercomChat from './components/IntercomChat/IntercomChat';
import CustomerWalkthrough from './components/Walkthrough/CustomerWalkthrough';
import { getProducts } from './app/ActionTypes/productActionTypes';
import AppCurtain from './components/AppCurtain/AppCurtain';
import { useEmailVerificationHandler } from './hooks/useEmailVerificationHandler';
import { useTrialActivationHandler } from './hooks/useTrialActivationHandler';
import { useCheckoutSuccessHandler } from './hooks/useCheckoutSuccessHandler';
import ConfirmActivationModal from './components/ConfirmActivationModal/ConfirmActivationModal';
import { trackGTMPageView } from './utils/gtm';
import { TokenService } from './services/secureTokenService';
import { selectIsTeamOnlyWithNoTeams } from './app/Slices/teamSlice';
import { useLogout } from './hooks/useLogout';

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const handleLogout = useLogout();

  const [appSideMenuCollapsed, setAppSideMenuCollapsed] = useState(
    localStorage.getItem('appSideMenuCollapsed') === 'true'
  );
  const { error } = useSelector((state: RootState) => state.subscription);
  const isTeamOnlyWithNoTeams = useSelector(selectIsTeamOnlyWithNoTeams);
  const userEmail = useSelector(
    (state: RootState) => state.team.personalAccount.record?.cirra_user_id
  );

  // Track GTM page view on route changes. Deferred via rAF so
  // react-helmet-async has time to update document.title after render.
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      trackGTMPageView(location.pathname, document.title);
    });
    return () => cancelAnimationFrame(rafId);
  }, [location.pathname]);

  // Handle Auth0 email verification redirect
  useEmailVerificationHandler();

  // Handle Stripe trial activation redirect (after adding payment method via portal)
  const { activationState, confirmActivation, cancelActivation } =
    useTrialActivationHandler();

  // Handle Stripe Checkout success redirect (after payment via Checkout)
  useCheckoutSuccessHandler();

  // Load general/global information
  useEffect(() => {
    dispatch(getProducts());
  }, []);

  /*
  useEffect(() => {
    // Use secure timeout that handles Auth0 initialization properly
    const cleanup = createSecureTimeout(
      isAuthLoading ? 200 : 50, // Longer delay if still loading
      [location.search, isAuthLoading, isAuthenticated, loginWithRedirect]
    );

    return cleanup;
  });
*/
  useEffect(() => {
    if (error instanceof ActionError) {
      Notify.error(error.message);
    } else if (typeof error === 'string') {
      Notify.error(error);
    }
  }, [error]);

  const setAppSideMenuState = (state: boolean) => {
    setAppSideMenuCollapsed(state);
    localStorage.setItem('appSideMenuCollapsed', state.toString());
  };

  if (isTeamOnlyWithNoTeams) {
    return (
      <Layout>
        <IntercomChat />
        <div className="authenticated-layout">
          <Header
            hasMenu={false}
            collapsed={appSideMenuCollapsed}
            toggleCollapsed={setAppSideMenuState}
          />
          <Layout>
            <Layout>
              <div
                id="main-content"
                className={`app-container ${appSideMenuCollapsed ? '' : ' side-menu-expanded'}`}
              >
                <Result
                  icon={<TeamOutlined />}
                  title="No Team Access"
                  subTitle={`You are not a member of any teams. Please contact your team administrator to request access. Logged in as ${TokenService.getUserEmail() || userEmail || ''}.`}
                  extra={
                    <Button type="primary" onClick={handleLogout}>
                      Log Out
                    </Button>
                  }
                />
              </div>
            </Layout>
          </Layout>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <IntercomChat />
      <div className="authenticated-layout">
        <AppCurtain />

        <Header
          hasMenu={false}
          collapsed={appSideMenuCollapsed}
          toggleCollapsed={setAppSideMenuState}
        />
        <Layout>
          <Layout>
            <div
              id="main-content"
              className={`app-container ${appSideMenuCollapsed ? '' : ' side-menu-expanded'}`}
            >
              <Outlet />
            </div>
          </Layout>
        </Layout>
        <LoaderProvider />

        <CustomerWalkthrough
          onComplete={(dontShowAgain) => {
            // Walkthrough completed, preferences automatically saved
          }}
          onSkip={() => {
            // Walkthrough skipped, preferences automatically saved
          }}
        />

        {activationState.priceId && (
          <ConfirmActivationModal
            open={activationState.showConfirmation}
            priceId={activationState.priceId}
            onConfirm={confirmActivation}
            onCancel={cancelActivation}
            loading={activationState.confirming}
          />
        )}
      </div>
    </Layout>
  );
};

export default App;
